// Sugalarm — small celebratory modal (cycle 34.19).
//
// Pops in over the current screen after a successful log on the mobile
// path. Shows a personalized "Congratulations!" title + a per-kind
// message ("You logged Apple with 25g of carbs.", "You just logged
// 30 min of bike riding.", …). Auto-dismisses after 3.6 s; tap the
// backdrop or the body to dismiss early. Designed to ride on top of
// the cycle-34.18 confetti burst.

import { useEffect, useState } from "react";

import { Logo } from "./Logo";

interface Props {
  /** Body text. The modal mounts when this becomes non-null and
   *  dismisses on null. */
  message: string | null;
  onDismiss: () => void;
  /** Optional auto-dismiss timeout. Default 3.6 s. */
  durationMs?: number;
}

export function CongratsModal({
  message,
  onDismiss,
  durationMs = 3600,
}: Props) {
  // Two-state mount machine: `message != null` opens, `null` triggers
  // the fade-out + late unmount. Same pattern as VoiceBanner.
  const [mounted, setMounted] = useState<boolean>(message != null);
  const visible = message != null;

  useEffect(() => {
    if (visible) {
      setMounted(true);
      const t = window.setTimeout(onDismiss, durationMs);
      return () => window.clearTimeout(t);
    }
    const t = window.setTimeout(() => setMounted(false), 240);
    return () => window.clearTimeout(t);
  }, [visible, durationMs, onDismiss]);

  if (!mounted) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Log confirmed"
      onClick={onDismiss}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 350,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: visible ? "rgba(0, 0, 0, 0.22)" : "transparent",
        backdropFilter: "blur(2px)",
        transition: "background 220ms ease-out",
        pointerEvents: visible ? "auto" : "none",
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(340px, 100%)",
          padding: "22px 24px",
          background: "var(--bg-elev)",
          border: "0.5px solid var(--accent)",
          borderRadius: 18,
          boxShadow: "0 16px 48px rgba(0, 0, 0, 0.4)",
          transform: visible
            ? "scale(1) translateY(0)"
            : "scale(0.94) translateY(8px)",
          opacity: visible ? 1 : 0,
          transition: "transform 240ms ease-out, opacity 200ms ease-out",
          textAlign: "center",
        }}
      >
        {/* Cycle 203: the Sugalarm brand shield centered at the top (reuses
            the existing <Logo> mark — no new asset). Shared modal, so this
            also appears on the mobile congrats path — an intentional,
            additive brand-polish touch. */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginBottom: 6,
          }}
        >
          <Logo size={52} />
        </div>
        <div
          style={{
            fontFamily: "var(--hero-font)",
            fontSize: 22,
            fontWeight: 700,
            letterSpacing: "-0.01em",
            color: "var(--accent)",
            marginBottom: 8,
            lineHeight: 1.1,
          }}
        >
          Congratulations!
        </div>
        <div
          style={{
            fontSize: 15,
            color: "var(--ink)",
            lineHeight: 1.4,
          }}
        >
          {message ?? ""}
        </div>
      </div>
    </div>
  );
}
